import {
  CellClassParams,
  CellMouseDownEvent,
  CellMouseOverEvent,
  CellRendererSelectorResult,
  CellStyle,
  ColDef,
  ColumnApi,
  Events,
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ISetFilterParams,
  ITooltipParams,
  Module,
  RowClassParams,
  RowClickedEvent,
  RowDoubleClickedEvent,
  RowNode,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  HeliosColumnTypes,
  heliosDefaultColWidth,
  heliosDefaultToolPanels,
  heliosMultiRowSelectionConfig,
  heliosServerSideRowConfig,
  NumberFilterOptionsWithAbsolute,
  NumberFilterOptionsWithoutEquals,
  setFilter,
  TextFilterOptionsWithContainsIn,
} from '@helios/ag-grid-config'
import {
  ClearwaterTransaction,
  ClearwaterTransactionsAdvancedOptions,
  ClearwaterTransactionsFilterContext,
  ClearwaterTransactionsSetValues,
  convertTimezone,
  ExternalTransaction,
  formatDates,
  formatDateTime,
  LinkedStatus,
  TransactionMatching,
} from '@helios/common-api'
import {
  AdvancedOptionsParams,
  FilterModalEvent,
  FilterModalService,
  FilterModel,
} from '@helios/filter-modal'
import {
  cacheLoadingFilterTooltip,
  cwTransactionsStatusBarPanel,
  ErrorStatusRenderer,
  errorStatusRendererKey,
  getColDefs,
  getGridState,
  HighlightingService,
  mergeBaseColDefsWithColumnState,
  TransactionsStatusBarModule,
  updateColDefs,
} from '@helios/generic-components'
import {
  HighlightRowsInCWTransactions,
  HotkeyDestroyFn,
  HotkeysService,
} from '@helios/hotkeys'
import {
  convertFields,
  convertFilters,
  countFilters,
  FilterType,
  handleColumnVisibilityChange,
  HeliosServerSideRequest,
  toHeliosRequest,
} from '@helios/server-side-util'
import {
  GridState,
  RootState,
  selectActiveSavedViewForGrid,
  selectActiveSession,
  selectActiveSessionReconDate,
  selectActualReconDate,
  selectCommandsMetrics,
  selectFirstFocusedAccountInfo,
  selectIsEnfusionAccount,
  selectIsHplAccount,
  selectLoadedForNonDefaultBasis,
  selectSavedViewExistsForGridKey,
  selectAnyCreatePermission,
  selectUserPolicy,
  TransactionCreatePermissions,
  SessionCacheStatus,
  setCommandsMetrics,
  setFocusedAssetId,
  userHasCommentaryAccess,
  UserPolicy,
  selectTransactionCreatePermissions,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  createFilterGroupsToolPanel,
  DensityLevel,
  FilterGroupsToolPanelParams,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import {
  downloadExcelFile,
  FilterTypeForQuickFilterOfLength,
  isNullOrEmpty,
} from '@helios/utils'
import {
  AssetIdListValidator,
  AssetIdValidator,
  IntegerIdValidator,
} from '@helios/validators'
import {
  CommentaryCountsService,
  CommentaryLiveAccountsService,
  CommentaryEntityRef,
  CommentaryEntitySearchService,
  CommentaryFollowService,
  CommentaryGridSelectionService,
  CommentCountCellRenderer,
  commentCountRendererKey,
  wireCommentaryGrid,
  wireCommentaryCounts,
  refilterOnThreadChanges,
  activeSessionAccountIds,
  getCommentaryColumnIfAccessible,
  getCommentaryContextMenuItems,
  transactionLinkId,
  COMMENTARY_COLUMN_ID,
  transactionCounts,
} from '@helios/comments'
import { Store } from '@ngrx/store'
import { AgGridApi, NfColumnTypes, NfTooltipParams } from '@northfork/ag-grid'
import {
  AgGridState,
  defaultTextFilterOptions,
  GridActionConfig,
} from '@northfork/ag-grid-angular'
import { composeFieldPath, formatDate, ISODateFormat } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { refreshSetValuesForAllSetFilterColumns } from 'libs/filter-modal/src/lib/utils/column-filter-utils'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
import { debounce } from 'lodash-es'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators'
import { ClearwaterTransactionsKey } from '../../../panel-key'
import { TransactionAvailableRestrictedColumns } from '../../models/clearwater-transaction'
import { GetTransactionsResponse } from '../../models/clearwater-transactions-action.model'
import { CwTxSummary } from '../../models/cw-tx-summary.model'
import { ExportExcelRequest } from '../../models/export-to-excel.model'
import { DateColumn } from '../../models/transaction-type.model'
import { ClearwaterTransactionsTaskService } from '../../services/clearwater-transactions-task.service'
import { ClearwaterTransactionsService } from '../../services/clearwater-transactions.service'
import { DifferenceGetterService } from '../../services/difference-getter/difference-getter.service'
import { MarginEffectSpinnerService } from '../../services/margin-effect-spinner.service'
import { getClearwaterTransactionsContextMenuItems } from '../../utils/clearwater-transactions-context-menu-utils'
import {
  ClearwaterTransactionColumnTypes,
  clearwaterTransactionColumnTypes,
} from '../../utils/clearwater-transactions-grid-helpers/clearwater-transaction-column-types'
import {
  CUSTODY_MATCHED_COLOR,
  TRADING_MATCHED_COLOR,
} from '../../utils/clearwater-transactions-grid-helpers/transaction-match-count'
import { getColumnValidations } from '../../utils/column-validation'
import { clearwaterTransactionCommentary } from './clearwater-transactions-commentary'

export interface CwTxSelectionEvent {
  cwTrans: ClearwaterTransaction[]
  newSelection: boolean
}

export type CreateTxButton = {
  onClick: () => void
  disabled: boolean
  name: string
  tooltip: string
}

export enum TransactionCategory {
  CASH_ENTRY = 'Cash Entry',
  TRADE = 'Trade',
  FX_SPOT_TRADE = 'Fx Spot Trade',
  CURRENCY_FORWARD = 'Currency Forward',
  WIRE_TRANSFER = 'Wire Transfer',
  DIRECT_TRANSFER = 'Direct Transfer',
  SECURITY_TRANSFER = 'Security Transfer',
}

/**
 * DO NOT CHANGE. Saved Views and Filter Groups depend on this key. Changing will result in users losing their saved
 * preferences for this grid.
 */
export const ClearwaterTransactionsMainGridKey = `${ClearwaterTransactionsKey}-MainGrid`

const field = composeFieldPath([] as ClearwaterTransaction[])

@Component({
  selector: 'hs-clearwater-transactions-grid',
  templateUrl: './clearwater-transactions-grid.component.html',
  styleUrls: ['./clearwater-transactions-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    HighlightingService,
    FilterModalService,
    DifferenceGetterService,
    TelemetryService,
  ],
})
export class ClearwaterTransactionsGridComponent implements OnInit, OnDestroy {
  private readonly destroySubject$ = new Subject<void>()
  private actualReconDate: Date | null = null
  actualReconDate$: Observable<Date | null> = this.store
    .select(selectActualReconDate)
    .pipe(
      map(reconDate => {
        if (reconDate == undefined) {
          return null
        }
        return reconDate
      }),
    )

  filterCount = 0
  newSelectionRefresh = false
  directMatchedTransactions: Set<RowNode> = new Set()
  sessionReconDate?: Date
  sessionStartDate?: Date
  loadedForNonDefaultBasis = false
  isSleeveAggregateLoaded = false
  isSleeveSimpleLoaded = false
  latestDragStartingRow?: string
  anyCreatePermission = true

  @Input()
  set externalTransactionSelection(extTrans: ExternalTransaction[]) {
    this.externalTransactionSelected = extTrans
    this.gridApi?.api.redrawRows()
  }

  @Input()
  set securityIdFilter(securityId: number | null | undefined) {
    this.setFilter(
      'secId',
      securityId != null
        ? {
            filterType: 'number',
            type: 'in',
            filter: String(securityId),
          }
        : undefined,
    )
  }

  @Input()
  set currencyFilter(currency: string | null | undefined) {
    let filter: unknown
    if (currency == null) {
      filter = {}
    } else {
      filter = {
        filterType: 'set',
        values: [currency],
      }
    }

    this.setFilter('currencyCode', filter)
  }

  _cacheLoaded = false

  externalTransactionSelected: ExternalTransaction[] = []
  private readonly cacheLoaded$ = new BehaviorSubject<boolean>(false)
  /** The session this grid was created for; a session switch replaces the grid. */
  private sessionAccountIds?: readonly number[]
  _cacheLoadStatus: SessionCacheStatus | null = null
  @Input()
  set cacheLoadStatus(value: SessionCacheStatus | null) {
    if (this._cacheLoadStatus === value) return
    if (this.cacheLoaded) return
    this._cacheLoadStatus = value
    this._cacheLoaded = this.cacheLoaded
    this.cacheLoaded$.next(this._cacheLoaded)

    this.resetCwTxSetValues()

    this.getColDefs().then(colDefs => {
      updateColDefs(colDefs, this.gridApi)
      if (this.cacheLoaded || this.isPartialCacheLoaded) {
        this.filterClearedToggle.enable()
        this.filterUnclearedToggle.enable()
        this.filterPostDateTodayToggle.enable({ emitEvent: false })
        this.hideFuturePostDatesToggle.enable({ emitEvent: false })
        this.quickSearchAssetIds.enable({ emitEvent: false })
        this.filterShowDiffsToggle.enable({ emitEvent: false })
      } else {
        this.filterClearedToggle.disable()
        this.filterUnclearedToggle.disable()
        this.filterPostDateTodayToggle.disable({ emitEvent: false })
        this.hideFuturePostDatesToggle.disable({ emitEvent: false })
        this.quickSearchAssetIds.disable({ emitEvent: false })
        this.filterShowDiffsToggle.disable({ emitEvent: false })
      }
    })
  }

  get cacheLoaded() {
    return this._cacheLoadStatus === SessionCacheStatus.DONE
  }

  get isPartialCacheLoaded() {
    return this._cacheLoadStatus === SessionCacheStatus.PARTIAL
  }

  @Input()
  set filteredExtTranIds(
    extTranIds: Array<number | undefined> | null | undefined,
  ) {
    this.setFilteredExtTranIds(extTranIds)
  }

  //this is the legacy tran id - number instead of UUID
  private _focusedTranId: number | undefined | null
  @Input()
  set focusedTranId(tranId: number | undefined | null) {
    if (!tranId) {
      return
    }

    this._focusedTranId = tranId
    this.gridApi?.api.setFilterModel(null)
    this.setLegacyTranIdFilter(this._focusedTranId)
  }

  // Not cleared like the legacy _pending* fields: the UUID filter has no
  // syncAssetIdsFromFilter side effect and the grid is destroyed with its panel.
  private _pendingFocusedTranIds: string[] | null | undefined
  @Input()
  set focusedTranIds(cwTranIds: string[] | null | undefined) {
    if (!cwTranIds || cwTranIds.length === 0) {
      return
    }
    this._pendingFocusedTranIds = cwTranIds
    if (this.gridApi) {
      this.gridApi.api.setFilterModel(null)
      this.setFilteredCwTranIds(cwTranIds)
    }
  }

  private _pendingLegacyTranIds: number[] | null | undefined
  @Input()
  set focusedLegacyTranIds(legacyTranIds: number[] | null | undefined) {
    if (!legacyTranIds || legacyTranIds.length === 0) {
      return
    }
    this._pendingLegacyTranIds = legacyTranIds
    if (this.gridApi) {
      this.gridApi.api.setFilterModel(null)
      this.setLegacyTranIdsFilter(legacyTranIds)
      this.syncAssetIdsFromFilter()
    }
  }

  private _pendingLegacyOrigIds: number[] | null | undefined
  @Input()
  set focusedLegacyOrigIds(legacyOrigIds: number[] | null | undefined) {
    if (!legacyOrigIds || legacyOrigIds.length === 0) {
      return
    }
    this._pendingLegacyOrigIds = legacyOrigIds
    if (this.gridApi) {
      this.gridApi.api.setFilterModel(null)
      this.setLegacyOrigIdsFilter(legacyOrigIds)
      this.syncAssetIdsFromFilter()
    }
  }

  private _focusedAssetId: string | undefined
  @Input()
  set focusedAssetId(assetId: string | undefined) {
    this._focusedAssetId = assetId
    this.setAssetIdFilter(this._focusedAssetId)
  }

  @Input()
  set rowsCreated(tranIds: string[] | null) {
    if (tranIds == null || tranIds.length == 0) return

    this.refreshServerSideStore()
  }

  @Input()
  set rowsUpdated(tranIds: string[] | null) {
    if (tranIds == null || tranIds.length == 0) return

    if (this.gridApi?.api.getDisplayedRowCount() === 0) return

    let needsRefresh = false
    if (tranIds.length === 1 && tranIds[0] === 'ALL') needsRefresh = true
    else {
      this.gridApi?.api.forEachNode((node: RowNode) => {
        if (node.data.tranId && tranIds.includes(node.data.tranId))
          needsRefresh = true
      })
    }

    if (needsRefresh) this.refreshServerSideStore()
  }

  @Input()
  sessionStartTime: number | undefined

  selectAllTxEnabled = new BehaviorSubject<boolean>(false)
  @Input()
  set isSelectAllRowsEnabled(enabled: boolean | null) {
    this.selectAllTxEnabled.next(enabled ? enabled : false)
  }

  selectAllTransactionsData: ClearwaterTransaction[] = []
  @Output()
  selectAllTransState = new EventEmitter<boolean>()

  selectAllExcludedTranRows: Set<string> = new Set()

  transactionsDataSummary = new BehaviorSubject<CwTxSummary>({})

  private readonly refreshServerSideStore = debounce(
    () => {
      this.resetCwTxSetValues()
      this.gridApi?.api.refreshServerSideStore({ purge: false })
      this.store
        .select(selectCommandsMetrics)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(metric => {
          if (metric.time > 0) {
            const commandDuration = Date.now() - metric.time
            this.telemetryService.emitCustomEvent(
              `Helios-${metric.command}-CommandDuration`,
              {
                loadDuration: commandDuration,
              },
            )
            this.store.dispatch(
              setCommandsMetrics({
                command: '',
                time: 0,
              }),
            )
          }
        })
    },
    100,
    { maxWait: 250 },
  )
  private readonly hotkeyDestroyStack: HotkeyDestroyFn[] = []

  readonly handleSelectionChanged = debounce(
    async (agGridApi: AgGridApi, newSelection: boolean) => {
      // clearing the all selected row summary data state when pressing esc key
      if (agGridApi.api.getSelectedRows().length === 0 && newSelection) {
        this.selectAllExcludedTranRows.clear()
        this.selectAllTransState.emit(false)
        this.transactionsDataSummary.next({})
        agGridApi.api.refreshCells()
      }
      if (this.selectAllTxEnabled.value) {
        const selectAllFilter = this.getSelectAllFilter()
        const cwTrans: ClearwaterTransaction[] = agGridApi.api.getSelectedRows()
        this.transactionsSelected.emit({
          cwTrans,
          newSelection,
        })
        let fullFilter = this.gridApi?.api.getFilterModel()
        if (fullFilter) {
          fullFilter = convertFilters(fullFilter)
          fullFilter['rowId'] = selectAllFilter.rowId
        } else {
          fullFilter = selectAllFilter
        }
        await this.transactionTaskService
          .getAllTransactionsSummary(
            fullFilter as FilterModel,
            this.appliedExternalFilterContext,
          )
          .then(result => {
            if (result.data !== undefined && result.data.length > 0) {
              this.transactionsDataSummary.next(result.data[0])
              agGridApi.api.dispatchEvent({
                type: Events.EVENT_SELECTION_CHANGED,
              })
            }
          })
        await this.transactionTaskService
          .selectAllTransactions(
            fullFilter as FilterModel,
            this.appliedExternalFilterContext,
          )
          .then(results => {
            this.selectAllTransactionsData = results.data ?? []
          })
      }
      if (!this.selectAllTxEnabled.value) {
        this.transactionsDataSummary.next({})
        const cwTrans: ClearwaterTransaction[] = agGridApi.api.getSelectedRows()
        if (newSelection && this.directMatchedTransactions.size > 0) {
          this.newSelectionRefresh = true
          agGridApi.api.redrawRows({
            rowNodes: Array.from(this.directMatchedTransactions.keys()),
          })
          this.directMatchedTransactions = new Set()
          this.newSelectionRefresh = false
        }
        this.transactionsSelected.emit({
          cwTrans,
          newSelection,
        })
      }
    },
    500,
  )

  @Output() createCashEntryAction = new EventEmitter<void>()
  @Output() createTradeEntryAction = new EventEmitter<void>()
  @Output() createFxSpotTradeAction = new EventEmitter<void>()
  @Output() createFxForwardAction = new EventEmitter<void>()
  @Output() createDirectTransferAction = new EventEmitter<void>()
  @Output() createWireTransferAction = new EventEmitter<void>()
  @Output() createSecurityTransferAction = new EventEmitter<void>()
  @Output() transactionsSelected = new EventEmitter<CwTxSelectionEvent>()

  transactionMatching = TransactionMatching

  /** @internal */
  gridApi?: AgGridApi

  readonly gridModules: Module[] = [
    FiltersToolPanelModule,
    ServerSideRowModelModule,
    RangeSelectionModule,
    TransactionsStatusBarModule,
  ]

  /** Table Properties Forms and Attributes Start*/

  quickSearchAssetIds = new FormControl('', AssetIdListValidator)
  filterPostDateTodayToggle = new FormControl(false)
  hideFuturePostDatesToggle = new FormControl(false)
  filterClearedToggle = new FormControl(false)
  filterUnclearedToggle = new FormControl(false)
  filterShowDiffsToggle = new FormControl(true)
  sortByAbsoluteValueToggle = new FormControl(false)
  showErrorIconsToggle = new FormControl(true)
  clearFiltersOnAccountLoadToggle = new FormControl(true)
  fxCurrencyFilteringToggle = new FormControl(true)
  fxCurrencyFilterItems: string[] = []
  fxCurrencyFilter = new FormControl([])

  /*@internal*/
  appliedExternalFilterContext: ClearwaterTransactionsFilterContext = {
    filterContext: {},
  }
  /** Table Properties Forms and Attributes End */
  applyHPL: Promise<boolean> = this.store
    .select(selectIsHplAccount)
    .pipe(take(1))
    .toPromise()

  isEnfusionAccount = false

  /** @internal */
  readonly gridActions: GridActionConfig<ClearwaterTransaction>[] =
    this.createGridActions()
  baseColDefs: Promise<ColDef[]> = this.createBaseColumnDefs()

  /**@internal */
  gridOptions$ = this.createGridOptions()

  availableRestrictedColumns = this.getAvailableRestrictedColumns()

  cacheLoadingFilterTooltip = cacheLoadingFilterTooltip

  /** @internal */
  cwTxSetValues$: Observable<ClearwaterTransactionsSetValues> =
    this.getCwTxSetValues()

  private gridState?: GridState

  createUnlinkedTxTooltip = 'Create'
  createTxButtons: CreateTxButton[] = this.getCreateTxButtons()

  private userPolicy?: UserPolicy
  hasExternalFileViewerPermissions = false
  hasAccessCommentary = false

  clearAllFiltersEnabled = false
  /*@internal */
  readonly filterChanged = () => {
    const filtersPresent = this.gridApi?.api.isAnyFilterPresent() ?? false
    const advancedFilterPresent =
      !!this.appliedExternalFilterContext?.filterContext?.fxCurrency
    this.clearAllFiltersEnabled = filtersPresent || advancedFilterPresent
  }

  get filterGroupsToolPanelParams(): FilterGroupsToolPanelParams<ClearwaterTransactionsFilterContext> {
    return {
      gridKey: ClearwaterTransactionsMainGridKey,
      getExternalFilters: () => ({
        filterContext: {},
        clearedTimestampToggle: this.filterClearedToggle.value,
        unclearedTimestampToggle: this.filterUnclearedToggle.value,
      }),
      setExternalFilters: () => this.applyExternalFilters(),
      cacheLoaded: this.cacheLoaded$,
    }
  }

  constructor(
    /** @internal*/
    readonly transactionsService: ClearwaterTransactionsService,
    /** @internal*/
    readonly transactionTaskService: ClearwaterTransactionsTaskService,
    /** @internal*/
    public highlightService: HighlightingService,
    /** @internal*/
    public filterModalService: FilterModalService,
    /** @internal*/
    readonly differenceGetterService: DifferenceGetterService,
    private readonly preferences: UserPreferencesService,
    private readonly store: Store<RootState>,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly hotkeysService: HotkeysService<HighlightRowsInCWTransactions>,
    private readonly toastService: ToastService,
    private readonly marginEffectSpinnerService: MarginEffectSpinnerService,
    private readonly telemetryService: TelemetryService,
    private readonly commentaryCountsService: CommentaryCountsService,
    private readonly commentaryLiveAccounts: CommentaryLiveAccountsService,
    private readonly commentaryFollowService: CommentaryFollowService,
    private readonly entitySearchService: CommentaryEntitySearchService,
    private readonly gridSelectionService: CommentaryGridSelectionService,
  ) {}

  ngOnInit() {
    this.getFxCurrencyFilterItems()

    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        this.sessionStartDate = session?.auxiliaryInfo?.LOAD_WINDOW_START_DATE
        this.sessionAccountIds = session?.loadedAccountIds
      })

    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1))
      .subscribe((sessionReconDate?: Date) => {
        this.sessionReconDate = sessionReconDate
      })
    this.store
      .select(selectLoadedForNonDefaultBasis)
      .pipe(take(1))
      .subscribe(loadedForNonDefaultBasis => {
        this.loadedForNonDefaultBasis = loadedForNonDefaultBasis ?? false
      })

    this.store
      .select(selectIsEnfusionAccount)
      .pipe(take(1))
      .subscribe(isEnfusionAccount => {
        this.isEnfusionAccount = isEnfusionAccount ?? false
      })

    this.filterModalService.setValidators({
      accountId: IntegerIdValidator,
      secIdentifier: AssetIdValidator,
    })

    this.actualReconDate$
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(reconDate => {
        this.actualReconDate = reconDate
      })

    this.quickSearchAssetIds.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroySubject$),
      )
      .subscribe(() => this.updateQuickSearchAssetIds(true))

    this.filterShowDiffsToggle.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroySubject$),
      )
      .subscribe(() => {
        this.showDiffsToggled()
        this.updateGridConfig()
      })
    this.store
      .select(selectFirstFocusedAccountInfo)
      .pipe(
        takeUntil(this.destroySubject$),
        distinctUntilChanged(),
        tap(selectedAccount => {
          if (selectedAccount) {
            this.isSleeveAggregateLoaded =
              'sleeveAggregate' === selectedAccount.accountType
            this.isSleeveSimpleLoaded =
              'sleeveSimple' === selectedAccount.accountType
          }
        }),
      )
      .subscribe()

    this.store
      .select(selectUserPolicy)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(policy => {
        this.userPolicy = policy ?? undefined
        this.hasExternalFileViewerPermissions =
          policy?.accessExternalFileViewerPanel ?? false
      })

    this.store
      .select(selectUserPolicy)
      .pipe(
        takeUntil(this.destroySubject$),
        map(policy => userHasCommentaryAccess(policy)),
        // keep listening only until access is first granted, then complete
        takeWhile(access => !access, true),
      )
      .subscribe(access => {
        this.hasAccessCommentary = access
        if (access) {
          this.wireCommentary()
        }
      })

    this.store
      .select(selectAnyCreatePermission)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(allowed => {
        this.anyCreatePermission = allowed
        this.createUnlinkedTxTooltip = allowed
          ? 'Create'
          : 'You do not have permission to create transactions'
      })

    this.store
      .select(selectTransactionCreatePermissions)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(permissions => {
        this.createTxButtons = this.getCreateTxButtons(permissions)
      })

    this.fxCurrencyFilter.valueChanges.subscribe((currency: string[]) => {
      this.onFxCurrencyFilterChange(currency)
    })

    this.registerHotkeys()
    this.sortByAbsoluteValueToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
      this.applyExternalFilters()
    })
    this.showErrorIconsToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })

    this.fxCurrencyFilteringToggle.valueChanges.subscribe(() => {
      this.fxCurrencyFilter.reset([], { emitEvent: false })
      delete this.appliedExternalFilterContext?.filterContext?.fxCurrency
      this.updateGridConfig()
    })
  }

  /**
   * DLITE-3849 — wire this grid into the commentary drawer (follow-a-chip row
   * focus + entitySearch/pickEntities sources). Clearwater is the panel
   * app.component activates for a `transaction` chip, and its row-node id isn't
   * the legacyOrigId, so revealGridRow filters the `legacyOrigId` column to
   * reveal the row (hasRowNodeId: false). Called at most once — the policy pipe
   * in ngOnInit completes after its first granting emission. Torn down via
   * destroySubject$.
   */
  private wireCommentary() {
    wireCommentaryGrid({
      follow: this.commentaryFollowService,
      search: this.entitySearchService,
      selection: this.gridSelectionService,
      panelKey: ClearwaterTransactionsKey,
      entityType: 'transaction',
      idColId: 'legacyOrigId',
      getApi: () => this.gridApi?.api,
      hasAccess: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      loadedRefs: () => this.collectLoadedTransactionRefs(),
      selectedRefs: () => this.collectSelectedTransactionRefs(),
      hasRowNodeId: false,
      idFilter: id => ({ filterType: 'number', type: 'in', filter: id }),
      toast: this.toastService,
      sessionAccountIds: () => this.sessionAccountIds,
      // Older threads link the transaction's UUID rather than its origTranId,
      // and the numeric legacyOrigId filter can never match a UUID.
      canFilter: id => transactionLinkId(id) != null,
    })
  }

  private collectLoadedTransactionRefs(): CommentaryEntityRef[] {
    const refs: CommentaryEntityRef[] = []
    this.gridApi?.api.forEachNode(node => {
      const id = transactionLinkId(
        (node.data as ClearwaterTransaction)?.legacyOrigId,
      )
      if (id != null) refs.push({ type: 'transaction', id })
    })
    return refs
  }

  private collectSelectedTransactionRefs(): CommentaryEntityRef[] {
    return (this.gridApi?.api.getSelectedNodes() ?? [])
      .map(node =>
        transactionLinkId((node.data as ClearwaterTransaction)?.legacyOrigId),
      )
      .filter((id): id is string => id != null)
      .map(id => ({ type: 'transaction' as const, id }))
  }

  ngOnDestroy() {
    this.unregisterHotkeys()
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
    this.filterModalService.destroy()
  }

  /*@internal*/
  _getActiveColumns() {
    return convertFields(
      this.gridApi?.columnApi,
      [],
      ['tranId', 'legacyOrigId', 'tranTypeCode', 'secId', 'accountId'],
    )
  }

  toggleErrors() {
    // maybe save this with the panel state
    this.gridApi?.api.redrawRows()
  }

  applyExternalFilters() {
    this.gridApi?.api.onFilterChanged()
  }
  /**
   * Add a filter to the Transaction list, that only includes Transactions that
   * have the same value in the same column
   *
   * @internal
   */
  filterByCell(params: GetContextMenuItemsParams) {
    const colId = params.column?.getColId() ?? ''
    if (params.node?.data == null || colId === '') {
      return
    }

    this.setFilter(colId as keyof ClearwaterTransaction, {
      type: 'equals',
      // TODO need to figure out which filterType is used for the column
      filterType: 'text',
      filter: params.value,
    })
  }

  /**
   * Add a filter to the Transaction list, that only includes Transactions that
   * have the same CUSIP(s)
   *
   * @internal
   */
  filterByCusip(transactions: ClearwaterTransaction[]) {
    this.setFilter('secIdentifier', {
      type: 'in',
      filter: transactions.map(t => t.extCusip).join(','),
    })
  }

  //#region GridConfig

  /**
   * @internal
   */
  async createGridOptions(): Promise<GridOptions> {
    return {
      ...heliosServerSideRowConfig,
      ...heliosMultiRowSelectionConfig,
      cacheBlockSize: 1000,
      allowContextMenuWithControlKey: true,
      columnDefs: await this.getColDefs(),
      context: {
        isSelectAllRowsEnabled: () => this.selectAllTxEnabled.value,
        summaryData: () => this.transactionsDataSummary.value,
        panelSettings: this.getCurrentPanelSettingsObject(),
      },
      columnTypes: {
        ...clearwaterTransactionColumnTypes,
      },
      components: {
        [errorStatusRendererKey]: ErrorStatusRenderer,
        [commentCountRendererKey]: CommentCountCellRenderer,
      },
      defaultColDef: {
        cellStyle: (params: CellClassParams) => this.getCellStyle(params),
        cellRenderer: undefined,
        filterParams: {
          buttons: ['apply', 'reset'],
        },
        sortable: false,
        tooltipValueGetter: params => this.getCellToolTip(params),
        tooltipComponentParams: {
          showTooltip: params =>
            getColumnValidations({
              data: params?.data,
              colDef: params?.colDef as ColDef,
            })
              ? true
              : undefined,
        } as NfTooltipParams,
        cellRendererSelector: params => {
          const validation = getColumnValidations(params)

          if (validation != null && this.showErrorIconsToggle.value) {
            return {
              component: errorStatusRendererKey,
            } as CellRendererSelectorResult
          }

          return undefined
        },
        width: heliosDefaultColWidth.Default,
      },
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            { label: 'Show Diffs', formControl: this.filterShowDiffsToggle },
            {
              label: 'Sort by Absolute Value',
              formControl: this.sortByAbsoluteValueToggle,
            },
            {
              label: 'Clear Filters on Load',
              formControl: this.clearFiltersOnAccountLoadToggle,
            },
            {
              label: 'Show Error Icons',
              formControl: this.showErrorIconsToggle,
            },
            {
              label: 'FX Currency Filtering',
              formControl: this.fxCurrencyFilteringToggle,
            },
          ],
        }),
        ...heliosDefaultToolPanels,
        createFilterGroupsToolPanel<ClearwaterTransactionsFilterContext>(
          this.filterGroupsToolPanelParams,
        ),
      ]),

      serverSideDatasource: this.createDatasource(),
      statusBar: cwTransactionsStatusBarPanel,
      getRowNodeId: (cwtx: ClearwaterTransaction) => cwtx.rowId,
      onGridReady: event => this.onGridReady(event),
      getContextMenuItems: params => this.getContextMenuItems(params),
      onCellContextMenu: event => this.handleOnCellContextMenu(),
      onRowDoubleClicked: event => this.onRowDoubleClicked(event),
      onRowClicked: event => this.handleOnRowClicked(event),
      onCellMouseOver: event => this.handleOnCellMouseOver(event),
      onCellMouseDown: event => this.handleOnCellMouseDown(event),
      onRowSelected: event => this.handleSelectionChanged(event, true),
      getRowClass: params => this.deduceRowClass(params),
      onColumnVisible: params =>
        handleColumnVisibilityChange(params, 'ClearwaterTransactions'),
      onFilterChanged: params => this.onFilterChanged(params),
      onModelUpdated: event => {
        this.handleSelectionChanged(event, false)
      },
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      rowClassRules: {
        'font-italic synthetic-transaction': params => {
          return (
            params.data?.tranSource === 'Synthetic' ||
            (this.sessionStartDate !== undefined &&
              new Date(params.data?.entryDate) < this.sessionStartDate)
          )
        },
      },
      ...disableChangeDetectionOnScroll(this.changeDetector),
    }
  }

  /*@internal*/
  onRowDoubleClicked(event: RowDoubleClickedEvent) {
    const cwTx: ClearwaterTransaction = event.data
    if (cwTx.availableActions?.includes('editTran')) {
      this.transactionTaskService.editClearwaterTransaction(cwTx)
    } else {
      this.transactionTaskService.viewClearwaterTransaction(cwTx)
    }
  }

  private createGridActions(): GridActionConfig<ClearwaterTransaction>[] {
    return [
      {
        label: 'Create',
        isPrimary: true,
        variant: 'success',
        buttons: [
          {
            label: 'Cash Entry',
            action: () => this.createCashEntryAction.emit(),
          },
          {
            label: 'Trade',
            action: () => this.createTradeEntryAction.emit(),
          },
          {
            label: 'FX Trade',
            action: () => this.createFxSpotTradeAction.emit(),
          },
          {
            label: 'Currency Forward',
            action: () => this.createFxForwardAction.emit(),
          },
          {
            label: 'Direct Transfer',
            action: () => this.createDirectTransferAction.emit(),
          },
          {
            label: 'Security Transfer',
            action: () => this.createSecurityTransferAction.emit(),
          },
        ],
      },
    ]
  }

  private getSelectedTransactions(): ClearwaterTransaction[] {
    return this.gridApi?.api.getSelectedRows() ?? []
  }

  getFxCurrencyFilterItems() {
    const setValues = this.getCwTxSetValues()
    setValues.subscribe(value => {
      if (value.currencyCodes && value.sellCurrencies)
        this.fxCurrencyFilterItems = [
          ...new Set([...value.sellCurrencies, ...value.currencyCodes]),
        ].sort((a, b) => a.localeCompare(b))
    })
  }

  onFxCurrencyFilterChange(currency: string[]) {
    if (!this.fxCurrencyFilteringToggle.value) return

    this.appliedExternalFilterContext = {
      ...this.appliedExternalFilterContext,
      filterContext: {
        ...this.appliedExternalFilterContext.filterContext,
        fxCurrency: currency,
      },
    }

    if (currency.length == 0)
      delete this.appliedExternalFilterContext?.filterContext?.fxCurrency
    if (!this.clearFiltersOnAccountLoadToggle.value) {
      this.updateGridConfig()
    }
    this.gridApi?.api.onFilterChanged()
  }

  async setFilteredExtTranIds(
    extTranIds: Array<number | undefined> | null | undefined,
  ) {
    this.clearAllFilters()
    await this.setFilter('extTranIds', {
      filterType: 'number',
      type: 'in',
      filter: extTranIds?.join(', '),
    })
    await this.setFilter('linkedStatus', {
      filterType: 'set',
      values: [
        LinkedStatus.MATCHED,
        LinkedStatus.CUSTODY_MATCHED,
        LinkedStatus.TRADING_MATCHED,
      ],
    })
  }

  async setFilteredCwTranIds(cwTranIds: string[] | null | undefined) {
    this.clearAllFilters()
    await this.setFilter('tranId', {
      filterType: 'text',
      type: 'in',
      filter: cwTranIds?.join(', '),
    })
  }

  /**
   * Right-click menu
   *
   * public for testing reasons
   *
   * @internal
   */
  getContextMenuItems(params: GetContextMenuItemsParams) {
    return [
      ...getClearwaterTransactionsContextMenuItems(
        params,
        this.externalTransactionSelected,
        this.transactionTaskService,
        this.highlightService,
        this.cacheLoaded,
        this.isSleeveAggregateLoaded,
        this.selectAllTxEnabled.value,
        this.selectAllTransactionsData,
        this.hasExternalFileViewerPermissions,
        this.isEnfusionAccount,
      ),
      ...getCommentaryContextMenuItems(
        this.store,
        this.hasAccessCommentary,
        clearwaterTransactionCommentary,
        params.node?.data,
      ),
    ]
  }

  getSetValues(key: keyof ClearwaterTransactionsSetValues) {
    return {
      filter: 'agSetColumnFilter',
      filterParams: {
        refreshValuesOnOpen: true,
        defaultToNothingSelected: true,
        values: params => {
          this.cwTxSetValues$.subscribe(values =>
            params.success((values[key] as string[]) ?? []),
          )
        },
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      } as ISetFilterParams,
    }
  }

  /**
   * Quick search for asset ID
   */
  updateQuickSearchAssetIds(assetIdTextSearch?: boolean) {
    if (this.quickSearchAssetIds.invalid) {
      return
    }

    const isAssetIdNullOrEmpty = isNullOrEmpty(this.quickSearchAssetIds.value)

    const assetIdsLength =
      this.quickSearchAssetIds.value?.split(',').length ?? 0

    const filter = {
      type: FilterTypeForQuickFilterOfLength(assetIdsLength),
      filterType: 'text',
      filter: this.quickSearchAssetIds.value,
    }

    if (!isAssetIdNullOrEmpty) {
      this.setFilter('secIdentifier', filter)
    } else if (assetIdTextSearch) {
      this.setFilter('secIdentifier', undefined)
    }
  }

  /**
   * clears the filter on undefined
   *
   * @internal
   */
  setLegacyTranIdFilter(tranId: number) {
    this.clearAllFilters()
    this.setFilter(
      'legacyTranId',
      tranId
        ? {
            filterType: 'number',
            type: 'equals',
            filter: tranId + '',
          }
        : undefined,
    )
  }

  /**
   * Filter by multiple legacy transaction IDs
   * @internal
   */
  setLegacyTranIdsFilter(tranIds: number[]) {
    this.clearAllFilters()
    this.setFilter('legacyTranId', {
      filterType: 'number',
      type: 'in',
      filter: tranIds.join(','),
    })
  }

  /**
   * Filter by multiple legacy orig IDs
   * @internal
   */
  setLegacyOrigIdsFilter(origIds: number[]) {
    this.clearAllFilters()
    this.setFilter('legacyOrigId', {
      filterType: 'number',
      type: 'in',
      filter: origIds.join(','),
    })
  }

  /**
   * Fetch secIdentifiers for the current filter and dispatch to External Transactions panel
   * @internal
   */
  private async syncAssetIdsFromFilter() {
    const filterModel = this.gridApi?.api.getFilterModel()
    if (!filterModel) {
      this._pendingLegacyTranIds = undefined
      this._pendingLegacyOrigIds = undefined
      return
    }

    const request: HeliosServerSideRequest<
      ClearwaterTransaction,
      ClearwaterTransactionsFilterContext
    > = {
      startRow: 0,
      endRow: 10000,
      fields: ['tranId', 'secIdentifier'],
      filter: convertFilters(filterModel),
      context: this.appliedExternalFilterContext,
      sort: [],
      group: { keys: [], fields: [] },
    }

    const response = await this.transactionsService.getTransactions(request)

    if (response.data && response.data.length > 0) {
      const secIdentifiers = new Set<string>()
      response.data.forEach(tx => {
        if (tx.secIdentifier) {
          secIdentifiers.add(tx.secIdentifier)
        }
      })

      if (secIdentifiers.size > 0) {
        this.store.dispatch(setFocusedAssetId([...secIdentifiers].join(',')))
      }
    }

    this._pendingLegacyTranIds = undefined
    this._pendingLegacyOrigIds = undefined
  }

  /*@internal*/
  setAssetIdFilter(assetId: string | undefined) {
    this.clearAllFilters()
    this.setFilter(
      'secIdentifier',
      assetId
        ? {
            type: 'containsIn',
            filter: assetId + '',
          }
        : undefined,
    )
  }

  /**
   * Exports the transactions to Excel same as visible in the CW table
   */
  exportCWTranToExcel() {
    const tableFilters = this.gridApi?.api.getFilterModel()
    let filters
    if (tableFilters) filters = convertFilters(tableFilters)
    const fields: string[] = []
    const colHeaderNames: string[] = []
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    this.gridApi?.columnApi.getAllDisplayedColumns().forEach(col => {
      const colField = col.getColId()
      if (colField === COMMENTARY_COLUMN_ID) return
      fields.push(colField)
      const headerName = col.getUserProvidedColDef()?.headerName
      if (headerName) colHeaderNames.push(headerName)
    })

    const request: ExportExcelRequest = {
      fields: fields,
      headers: colHeaderNames,
      filters: filters as FilterModel,
      userTimeZone: userTimeZone,
      context: {
        ...this.appliedExternalFilterContext,
      },
    }

    this.transactionsService.exportToExcel(request).subscribe(
      response => {
        downloadExcelFile(response, 'Clearwater Transactions')
        this.toastService.success(
          "Export to Excel completed successfully. The exported file is available in your browser's Downloads",
          {
            closeButton: true,
            timeOut: 3000,
          },
        )
      },
      () => {
        this.toastService.error(
          'Export to Excel failed. Please try again.',
          '',
          {
            closeButton: true,
            timeOut: 3000,
            disableTimeOut: false,
          },
        )
      },
    )
    this.toastService.warning(
      'Export to Excel is in progress. It may take more than 30 seconds. Please wait.',
      {
        closeButton: true,
        timeOut: 3000,
      },
      'In Progress',
    )
  }

  createActionButton(transactionCategory: TransactionCategory) {
    switch (transactionCategory) {
      case TransactionCategory.CASH_ENTRY:
        this.createCashEntryAction.emit()
        break
      case TransactionCategory.TRADE:
        this.createTradeEntryAction.emit()
        break
      case TransactionCategory.FX_SPOT_TRADE:
        this.createFxSpotTradeAction.emit()
        break
      case TransactionCategory.CURRENCY_FORWARD:
        this.createFxForwardAction.emit()
        break
      case TransactionCategory.WIRE_TRANSFER:
        this.createWireTransferAction.emit()
        break
      case TransactionCategory.DIRECT_TRANSFER:
        this.createDirectTransferAction.emit()
        break
      case TransactionCategory.SECURITY_TRANSFER:
        this.createSecurityTransferAction.emit()
        break
      default:
        break
    }
  }

  async getColDefs(): Promise<ColDef[]> {
    return getColDefs(this.cacheLoaded, await this.baseColDefs, this.gridApi)
  }

  /**
   * @internal
   */
  async createBaseColumnDefs(): Promise<ColDef[]> {
    const hplColumnType = (await this.applyHPL)
      ? HeliosColumnTypes.HPLNumber
      : NfColumnTypes.Number
    const baseColDefs: ColDef[] = [
      ...(await getCommentaryColumnIfAccessible(
        this.store,
        clearwaterTransactionCommentary,
        () => this.hasAccessCommentary,
        this.commentaryCountsService,
        transactionCounts,
      )),
      {
        ...this.getSetValues('linkedStatus'),
        headerName: 'Linked Status',
        field: field('linkedStatus'),
        sortable: true,
        editable: !this.isSleeveSimpleLoaded,
        cellClass: () => (this.isSleeveSimpleLoaded ? 'disable-cols' : ''),
        tooltipValueGetter: params =>
          this.isSleeveSimpleLoaded
            ? 'Please load from the Parent Portfolio to see accurate Match Statuses.'
            : params.data.linkedStatus,
      },
      {
        field: field('custodyMatches'),
        headerName: 'Custody Matches',
        type: ClearwaterTransactionColumnTypes.TransactionMatchCount,
        cellStyle: params => ({
          ...this.getCellStyle(params),
          color: CUSTODY_MATCHED_COLOR,
        }),
        editable: !this.isSleeveSimpleLoaded,
        cellClass: () => (this.isSleeveSimpleLoaded ? 'disable-cols' : ''),
        tooltipValueGetter: params =>
          this.isSleeveSimpleLoaded
            ? 'Please load from the Parent Portfolio to see Custody Matches.'
            : params.value
            ? `There are ${params.value} matches.`
            : '',
      },
      {
        field: field('tradingMatches'),
        headerName: 'Trading Matches',
        type: ClearwaterTransactionColumnTypes.TransactionMatchCount,
        cellStyle: params => ({
          ...this.getCellStyle(params),
          color: TRADING_MATCHED_COLOR,
        }),
      },
      {
        ...this.getSetValues('accountNames'),
        field: field('accountName'),
        headerName: 'Account Name',
        sortable: true,
      },
      {
        ...this.getSetValues('accountIds'),
        field: field('accountId'),
        headerName: 'Account ID',
        type: NfColumnTypes.Identifier,
        valueFormatter: undefined,
        width: heliosDefaultColWidth.Id,
        hide: true,
        sortable: true,
      },

      {
        field: field('xferAccountId'),
        headerName: 'Transfer Account ID',
        valueFormatter: params => (params.value != -1 ? params.value : ''),
        width: heliosDefaultColWidth.Id,
        type: NfColumnTypes.Identifier,
        filterParams: {
          filterOptions: NumberFilterOptionsWithoutEquals,
        },

        sortable: true,
      },
      {
        headerName: 'Asset ID',
        field: field('secIdentifier'),
        width: heliosDefaultColWidth.SecurityIdentifier,
        sortable: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        cellStyle: params => this.getCellStyle(params, true),
      },
      {
        field: field('cwanIdentifier'),
        headerName: 'Asset Identifier',
        initialHide: false,
        sortable: true,
        width: 120,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: field('secCusip'),
        headerName: 'Sec Cusip',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: field('extCusip'),
        headerName: 'Ext Cusip',
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: field('secIsin'),
        headerName: 'Sec Isin',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        ...this.getSetValues('secTypeCodes'),
        field: field('secTypeCode'),
        headerName: 'Sec Type Code',
        sortable: true,
      },
      {
        field: field('secSubType'),
        headerName: 'Sec Sub Type',
      },
      {
        ...this.getSetValues('tranTypeCodes'),
        field: field('tranTypeCode'),
        headerName: 'Tran Type Code',
        sortable: true,
        cellStyle: params => this.getCellStyle(params, true),
        valueFormatter: params => {
          const value: string = params.value ?? ''
          if (params.node == null) return value
          const { userName, typeLabel, tranTypeCode } = params.node
            .data as ClearwaterTransaction
          if (
            userName === 'perseus' &&
            typeLabel === 'Cash Consolidation' &&
            tranTypeCode === 'WIRE'
          )
            return 'CCWIRE'
          return value
        },
      },
      {
        field: field('extReconDate'),
        headerName: 'Ext Recon Date',
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        sortable: true,
      },
      {
        field: field('entryDate'),
        headerName: 'Entry Date',
        type: NfColumnTypes.Date,
        sortable: true,
        width: heliosDefaultColWidth.DateCol,
        sort: 'desc',
        sortIndex: 0,
        cellStyle: params => this.getCellStyle(params, true),
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
      },
      {
        field: field('settleDate'),
        headerName: 'Settle Date',
        type: NfColumnTypes.Date,
        sortable: true,
        width: heliosDefaultColWidth.DateCol,
        cellStyle: params => this.getCellStyle(params, true),
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
      },
      {
        field: field('postDate'),
        headerName: 'Post Date',
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        sortable: true,
        cellStyle: params => this.getCellStyle(params, true),
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
      },
      {
        field: field('clearedTimestamp'),
        headerName: 'Cleared Timestamp',
        type: HeliosColumnTypes.ShortDateTime,
        width: heliosDefaultColWidth.DateTime,
        sortable: true,
        tooltipValueGetter: params => {
          return formatDateTime(params.value)
        },
      },
      {
        field: field('units'),
        headerName: 'Units',
        type: hplColumnType,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
        sortable: true,
        cellStyle: params => this.getCellStyle(params, true),
      },
      {
        field: field('price'),
        headerName: 'Price',
        type: NfColumnTypes.Number,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
        cellStyle: params => this.getCellStyle(params, true),
        sortable: true,
      },
      {
        field: field('amount'),
        headerName: 'Amount',
        type: hplColumnType,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
        sortable: true,
        cellStyle: params => this.getCellStyle(params, true),
      },
      {
        ...this.getSetValues('currencyCodes'),
        field: field('currencyCode'),
        headerName: 'CurrencyCode',
        cellStyle: params => this.getCellStyle(params, true),
        sortable: true,
      },
      {
        ...this.getSetValues('sellCurrencies'),
        field: field('sellCurrency'),
        headerName: 'Sell Currency',
        sortable: true,
      },
      {
        field: field('accrued'),
        headerName: 'Accrued',
        type: NfColumnTypes.Number,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
        cellStyle: params => this.getCellStyle(params, true),
        sortable: true,
      },
      {
        field: field('commission'),
        headerName: 'Commission',
        type: NfColumnTypes.Number,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
        cellStyle: params => this.getCellStyle(params, true),
        sortable: true,
      },
      {
        field: field('originationFee'),
        headerName: 'Loan Origination Fee',
        type: NfColumnTypes.Number,
        sortable: false,
        cellStyle: params => this.getCellStyleWithDiff(params),
      },
      {
        field: field('prepaidInterest'),
        headerName: 'Loan Prepaid Interest',
        type: NfColumnTypes.Number,
        sortable: true,
        cellStyle: params => this.getCellStyleWithDiff(params),
      },
      {
        field: field('interestDue'),
        headerName: 'Loan Past Due Interest',
        type: NfColumnTypes.Number,
        sortable: true,
        cellStyle: params => this.getCellStyleWithDiff(params),
      },
      {
        field: field('sellUnits'),
        headerName: 'Sell Units',
        type: hplColumnType,
        sortable: true,
      },
      {
        field: field('typeLabel'),
        headerName: 'Type Label',
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: field('extDescription'),
        headerName: 'Ext Description',
        sortable: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        ...this.getSetValues('userNames'),
        field: field('userName'),
        headerName: 'User Name',
        filter: true,
        sortable: true,
      },
      {
        field: field('legacyOrigId'),
        headerName: 'Orig Transaction ID',
        type: NfColumnTypes.Identifier,
        filterParams: {
          filterOptions: NumberFilterOptionsWithoutEquals,
        },
        sortable: true,
      },
      {
        field: field('legacyTranId'),
        headerName: 'Transaction ID',
        type: NfColumnTypes.Identifier,
        filterParams: {
          filterOptions: NumberFilterOptionsWithoutEquals,
        },
        sortable: true,
      },
      {
        field: field('tranId'),
        headerName: 'Transaction UUID',
        hide: true,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: field('secId'),
        headerName: 'Security ID',
        hide: true,
        type: NfColumnTypes.Identifier,
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithoutEquals,
        },
      },
      {
        field: field('secFigi'),
        headerName: 'Sec Figi',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: field('secSedol'),
        headerName: 'Sec Sedol',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: field('secDescription'),
        headerName: 'Sec Description',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: field('tradeDate'),
        headerName: 'Trade Date',
        hide: true,
        type: NfColumnTypes.Date,
        filter: false,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        sortable: true,
      },
      {
        headerName: 'Fx Rate',
        field: field('sellToBuyRate'),
        hide: true,
        type: NfColumnTypes.Number,
        filter: false,
        sortable: true,
      },
      {
        field: field('sourceName'),
        headerName: 'Source Name',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: field('marginEffect'),
        headerName: 'Margin Effect',
        type: NfColumnTypes.Number,
        hide: true,
        sortable: true,
        cellClass: cellClassParams => {
          if (this.loadedForNonDefaultBasis) {
            return 'greyed-out-cell'
          }
          if (cellClassParams.node.id == null) {
            return ''
          }

          return this.marginEffectSpinnerService.isUpdating(
            cellClassParams.node.id,
          )
            ? 'helios-ag-grid-cell-spinner'
            : ''
        },
      },
      {
        field: field('extAggregateInfo'),
        headerName: 'Ext Aggregate Info',
        hide: true,
        tooltipValueGetter: params => {
          return params.value?.replace(/,"/g, '\n').replace(/"/g, '') ?? ''
        },
        sortable: true,
      },
      {
        field: field('extAccrued'),
        headerName: 'Ext Accrued',
        hide: true,
        type: NfColumnTypes.Number,
        sortable: true,
      },
      {
        field: field('extCurrencyCode'),
        headerName: 'Ext Currency Code',
        hide: true,
        sortable: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
      },
      {
        field: field('extPostDate'),
        headerName: 'Ext Post Date',
        hide: true,
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        sortable: true,
      },
      {
        field: field('extIdentifier'),
        headerName: 'Ext Identifier',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: field('extTranIds'),
        headerName: 'Ext Transaction IDs',
        filterParams: {
          filterOptions: ['in', 'notIn'],
        },
        hide: true,
        type: NfColumnTypes.Identifier,
        sortable: true,
      },
      {
        field: field('createdTimestamp'),
        headerName: 'Created Timestamp',
        hide: true,
        type: HeliosColumnTypes.CurrentTimeZoneDate,
        sortable: true,
        tooltipValueGetter: params => {
          return convertTimezone(params.value)
        },
      },

      {
        field: field('contractIdentifier'),
        headerName: 'Contract ID',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: field('dataMap'),
        headerName: 'Data Map',
        hide: true,
        tooltipValueGetter: params => {
          return params.value?.replace(/,"/g, '\n').replace(/"/g, '') ?? ''
        },
        sortable: true,
      },
      {
        field: field('collateralBroker'),
        headerName: 'Collateral Broker',
        hide: true,
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
        },
        sortable: true,
      },
      {
        field: field('tranNote'),
        headerName: 'Tran Note',
        hide: true,
        cellRendererSelector: undefined,
        filterParams: {
          ...defaultTextFilterOptions,
          closeOnApply: true,
        },
        sortable: true,
      },
      {
        headerName: 'Transaction Source',
        field: field('tranSource'),
        hide: true,
        sortable: true,
      },
      {
        headerName: 'Ticker',
        field: field('secTicker'),
        sortable: true,
      },
      {
        headerName: 'Spec Lot Match',
        field: field('hasSpecLotMatches'),
        type: HeliosColumnTypes.Boolean,
        sortable: false,
      },
      {
        headerName: 'Custody Amount Difference',
        field: field('custodyAmountDifference'),
        type: NfColumnTypes.Number,
        valueFormatter: params =>
          this.formatAmountDifference(Number(params.value)),
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        headerName: 'Trading Amount Difference',
        field: field('tradingAmountDifference'),
        type: NfColumnTypes.Number,
        valueFormatter: params =>
          this.formatAmountDifference(Number(params.value)),
        sortable: true,
        filterParams: {
          filterOptions: NumberFilterOptionsWithAbsolute,
        },
      },
      {
        field: field('isCapitalize'),
        headerName: 'Capitalized',
        type: HeliosColumnTypes.Boolean,
        sortable: false,
        cellStyle: params => this.getCellStyleWithDiff(params),
      },
      {
        field: field('elmClientTranId'),
        headerName: 'ELM Client Transaction ID',
        sortable: true,
      },
      {
        field: field('elmLotEffects'),
        headerName: 'ELM Lot Effects',
        sortable: true,
      },
      {
        field: field('elmSecId'),
        headerName: 'ELM Security ID',
        type: NfColumnTypes.Identifier,
        sortable: true,
      },
      {
        field: field('basis'),
        headerName: 'Basis',
        type: NfColumnTypes.Identifier,
        sortable: true,
      },
    ]

    const savedViewExists = await this.store
      .pipe(
        selectSavedViewExistsForGridKey(ClearwaterTransactionsMainGridKey),
        takeUntil(this.destroySubject$),
        take(1),
      )
      .toPromise()

    if (!savedViewExists) {
      return baseColDefs
    }

    const savedColumnState = await this.store
      .pipe(
        takeUntil(this.destroySubject$),
        selectActiveSavedViewForGrid(ClearwaterTransactionsMainGridKey),
        take(1),
        map(savedView => savedView.savedView.columnState),
      )
      .toPromise()

    let colDefs = mergeBaseColDefsWithColumnState(baseColDefs, savedColumnState)

    const availableColumns = await this.availableRestrictedColumns
    if (availableColumns) {
      const columnsToHide = availableColumns
        .filter(column => !column.isAvailable)
        .map(column => column.column)

      colDefs = colDefs.map(colDef => {
        if (colDef.colId && columnsToHide.includes(colDef.colId)) {
          return {
            ...colDef,
            hide: true,
            suppressMoveable: true,
            suppressMenu: true,
            suppressColumnsToolPanel: true,
          }
        }
        return colDef
      })
    }

    return colDefs
  }

  /* @internal */

  handleOnRowClicked(rowClickedEvent: RowClickedEvent) {
    const event = rowClickedEvent.event as PointerEvent
    if (this.selectAllTransState && event?.ctrlKey) {
      this.setDeselectedRowsFromSelectAllFilter(rowClickedEvent.data.rowId)
    } else {
      this.selectAllExcludedTranRows.clear()
      this.selectAllTransState.emit(false)
      this.transactionsDataSummary.next({})
    }
    if (event?.altKey) {
      this.quickSearchAssetIds.setValue(rowClickedEvent.data.secIdentifier)
      this.store.dispatch(setFocusedAssetId(rowClickedEvent.data.secIdentifier))
    }
  }

  handleOnCellMouseOver(mouseOverEvent: CellMouseOverEvent) {
    if (UserPreferencesService.getUseOldGridMultiRow()) return
    const mouseEvent = mouseOverEvent.event as MouseEvent

    if (mouseEvent.buttons === 1) {
      if (!mouseEvent.ctrlKey) {
        this.selectAllExcludedTranRows.clear()
        this.selectAllTransState.emit(false)
        this.transactionsDataSummary.next({})
      } else {
        if (this.selectAllTxEnabled) {
          if (this.latestDragStartingRow != undefined) {
            this.setDeselectedRowsFromSelectAllFilter(
              this.latestDragStartingRow,
            )
            this.latestDragStartingRow = undefined
          }
          this.setDeselectedRowsFromSelectAllFilter(mouseOverEvent.data.rowId)
        }
      }
    }
  }
  handleOnCellMouseDown(mouseDownEvent: CellMouseDownEvent) {
    if (UserPreferencesService.getUseOldGridMultiRow()) return
    const mouseEvent = mouseDownEvent.event as MouseEvent
    if (mouseEvent.ctrlKey && this.selectAllTxEnabled) {
      this.latestDragStartingRow = mouseDownEvent.data.rowId
    }
  }

  handleOnCellContextMenu() {
    if (this.selectAllTxEnabled.value) {
      const selectAllFilter = this.getSelectAllFilter()
      let fullFilter = this.gridApi?.api.getFilterModel()
      if (fullFilter) {
        fullFilter = convertFilters(fullFilter)
        fullFilter['rowId'] = selectAllFilter.rowId
      } else {
        fullFilter = selectAllFilter
      }
      this.transactionTaskService
        .selectAllTransactions(
          fullFilter as FilterModel,
          this.appliedExternalFilterContext,
        )
        .then(result => {
          const currentTrans = this.selectAllTransactionsData.length
          this.selectAllTransactionsData = result.data ?? []
          if (currentTrans != this.selectAllTransactionsData.length) {
            this.gridApi?.api.hidePopupMenu()
          }
        })
    }
  }

  formatAmountDifference(value: number): string {
    if (isNaN(value)) {
      return ''
    } else {
      return value.toLocaleString('en', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      })
    }
  }

  // TODO: PVAL-9716 verify that match and unmatch are in available actions
  // [field('tranId'), 'custodyLinkedStatus', 'tradingLinkedStatus'],
  /** @internal*/
  createDatasource(): IServerSideDatasource {
    this.highlightService.clearAll()
    return {
      getRows: async (params: IServerSideGetRowsParams) => {
        try {
          this.filterCount =
            countFilters(
              params.api,
              this.appliedExternalFilterContext.filterContext,
            ) ?? 0

          this.gridState = getGridState(this.gridApi)
          const cwTransResponse =
            await this.transactionsService.getTransactions(
              toHeliosRequest<
                ClearwaterTransaction,
                ClearwaterTransactionsFilterContext
              >(
                params,
                [],
                clearwaterTransactionCommentary.mandatoryFields(
                  field('legacyTranId'),
                  field('tranId'),
                  field('secId'),
                  field('tranSource'),
                  field('currencyCode'),
                  field('extTranIds'),
                  field('userName'),
                  field('typeLabel'),
                  field('rowId'),
                  field('linkedStatus'),
                  field('amountByCurrencyCode'),
                  field('accountName'),
                  field('custodyAmountDifference'),
                  field('tradingAmountDifference'),
                  field('extCusip'),
                  field('basis'),
                ),
                this.appliedExternalFilterContext,
                this.sortByAbsoluteValueToggle.value,
              ),
              this.cacheLoaded$,
            )
          const cwTrans = cwTransResponse.data ?? []
          const totalRows = (params.request.startRow || 0) + cwTrans.length
          this.adjustColumnHeaders(cwTransResponse)

          params.success({
            rowData: cwTrans,
            rowCount:
              totalRows < (params?.request?.endRow ?? totalRows)
                ? totalRows
                : undefined,
          })

          if (this.sessionStartTime) {
            const now = Date.now()
            this.telemetryService.emitCustomEvent(
              'heliosSessionCwTransactionLoadTime',
              {
                durationMs: now - this.sessionStartTime,
              },
            )
            console.log('Session Load Time', now - this.sessionStartTime)
            this.sessionStartTime = undefined
          }

          if (this.filterShowDiffsToggle.value) {
            this.differenceGetterService.setRowDifferences(cwTrans, params.api)
          }
        } catch (e) {
          params.fail()
        }
      },
    }
  }

  private adjustColumnHeaders(cwTransResponse: GetTransactionsResponse) {
    this.gridApi?.columnApi.getAllColumns()?.forEach(col => {
      const fieldName = col.getColDef().field ?? 'undefined'
      const status =
        cwTransResponse.gridLoadStatus?.specialColumnStatuses[fieldName]
      const waiting =
        status?.toLowerCase() == SessionCacheStatus.STARTED.toLowerCase()
      col.getColDef().headerComponentParams = {
        menuIcon: waiting ? 'mdi mdi-timer-sand text-warning' : '',
      }
    })

    this.gridApi?.api.refreshHeader()
  }

  private resetCwTxSetValues() {
    this.cwTxSetValues$ = this.getCwTxSetValues()
  }

  private getCwTxSetValues() {
    return this.transactionsService.getSetValues().pipe(take(1), shareReplay(1))
  }

  updateGridConfig() {
    if (this.cacheLoaded) {
      const panelSettingsChangedEvent = new CustomEvent(
        'panelSettingsChanged',
        {
          detail: {
            api: this.gridApi,
            panelSettings: this.getCurrentPanelSettingsObject(),
          } as WorkspaceUpdateEventObject,
        },
      )
      this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
    }
  }
  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['sortByAbsolute'] = this.sortByAbsoluteValueToggle.value
    panelSettings['showErrorIcons'] = this.showErrorIconsToggle.value
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    panelSettings['showDiffs'] = this.filterShowDiffsToggle.value
    panelSettings['allowFxCurrencyFilter'] =
      this.fxCurrencyFilteringToggle.value
    if (!this.clearFiltersOnAccountLoadToggle.value) {
      panelSettings['fxCurrencyFilter'] = this.fxCurrencyFilter.value
    }
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('sortByAbsolute' in panelSettings)
        this.sortByAbsoluteValueToggle.patchValue(
          Boolean(panelSettings.sortByAbsolute),
          { emitEvent: false },
        )

      if ('showErrorIcons' in panelSettings)
        this.showErrorIconsToggle.patchValue(
          Boolean(panelSettings.showErrorIcons),
          { emitEvent: false },
        )

      if ('clearFiltersOnAccountLoad' in panelSettings)
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )

      if ('showDiffs' in panelSettings)
        this.filterShowDiffsToggle.setValue(Boolean(panelSettings.showDiffs), {
          emitEvent: false,
        })

      if ('allowFxCurrencyFilter' in panelSettings)
        this.fxCurrencyFilteringToggle.setValue(
          Boolean(panelSettings.allowFxCurrencyFilter),
          { emitEvent: false },
        )
      if (
        'fxCurrencyFilter' in panelSettings &&
        !this.clearFiltersOnAccountLoadToggle.value
      ) {
        this.fxCurrencyFilter.setValue(panelSettings.fxCurrencyFilter)
      }
    }
  }

  private async updateAvailableColumns(columnApi: ColumnApi): Promise<void> {
    const columns = await this.availableRestrictedColumns
    const columnsToHide = columns
      .filter(column => !column.isAvailable)
      .map(column => column.column)

    const updatedColumns = columnsToHide.map(column => {
      return {
        colId: column,
        hide: true,
      }
    })

    columnApi.applyColumnState({
      state: updatedColumns,
      applyOrder: true,
    })
  }

  /** @internal */
  async onGridReady(event: GridReadyEvent) {
    this.gridApi = event

    wireCommentaryCounts<ClearwaterTransaction>({
      changes$: this.commentaryCountsService.changes$,
      getApi: () => event.api,
      hasAccessForLoad: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      accountIdOf: row => row?.accountId,
      loadCounts: ids =>
        this.commentaryCountsService.ensureEntityCountsLoaded(ids),
      live: { accounts: this.commentaryLiveAccounts, surface: 'entity' },
    })
    refilterOnThreadChanges<ClearwaterTransaction>(
      () => event.api,
      this.destroySubject$,
      {
        accountIdOf: row => row?.accountId,
        queriedAccountIds: activeSessionAccountIds(
          this.store,
          this.destroySubject$,
        ),
      },
    )

    updateColDefs(await this.getColDefs(), this.gridApi)

    // Apply any pending legacy ID filters that were set before grid was ready
    // and sync to External Transactions panel
    const hasPendingLegacyFilter =
      (this._pendingLegacyTranIds && this._pendingLegacyTranIds.length > 0) ||
      (this._pendingLegacyOrigIds && this._pendingLegacyOrigIds.length > 0)

    if (this._pendingLegacyTranIds && this._pendingLegacyTranIds.length > 0) {
      this.setLegacyTranIdsFilter(this._pendingLegacyTranIds)
    }
    if (this._pendingLegacyOrigIds && this._pendingLegacyOrigIds.length > 0) {
      this.setLegacyOrigIdsFilter(this._pendingLegacyOrigIds)
    }
    if (this._pendingFocusedTranIds && this._pendingFocusedTranIds.length > 0) {
      this.setFilteredCwTranIds(this._pendingFocusedTranIds)
    }

    // If we applied a legacy filter from URL, sync asset IDs to External panel
    if (hasPendingLegacyFilter) {
      this.syncAssetIdsFromFilter()
    }

    enableHeliosMultiRowDragSelection(event.api)
    enableHeliosCopyBehavior(event.api)
    this.gridApi.api.addEventListener(
      Events.EVENT_FILTER_CHANGED,
      this.filterChanged,
    )

    this.filterModalService
      .registerGrid(
        this.gridApi.api,
        ClearwaterTransactionsKey,
        ClearwaterTransactionsMainGridKey,
        [COMMENTARY_COLUMN_ID],
        undefined,
        () => ({
          context: {
            filterContext: {},
          },
          clearedTimestampToggle: this.filterClearedToggle.value,
          unclearedTimestampToggle: this.filterUnclearedToggle.value,
        }),
      )
      .pipe(
        takeUntil(this.destroySubject$),
        map(
          x =>
            x as FilterModalEvent<
              | AdvancedOptionsParams<
                  ClearwaterTransactionsFilterContext,
                  ClearwaterTransactionsAdvancedOptions
                >
              | undefined
            >,
        ),
      )
      .subscribe(x => {
        if (!x.payload) {
          return
        }

        this.applyExternalFilters()
      })

    this.setupPostDateTodayUpdate()
    this.setupPostDateNotFutureUpdate()
    this.subscribeClearedUnclearedGridUpdate()

    this.marginEffectSpinnerService.registerGridApi(event)

    /* why move this later ? so that all filter change events are called before workspace is rendered and only if workspace
    has filter model it ever again calls filter model change not otherwise avoiding unnecessary race conditions */
    const agGridStateSaved = await this.savedViewService.manageSavedView(
      event,
      ClearwaterTransactionsMainGridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )

    this.applyGridConfig(agGridStateSaved)
    this.filterChanged()
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    await this.updateAvailableColumns(event.columnApi)
    refreshSetValuesForAllSetFilterColumns(event)
  }

  subscribeClearedUnclearedGridUpdate() {
    this.filterClearedToggle.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(clearedTimestampToggle =>
        this.setClearedTimestampFilter(
          clearedTimestampToggle,
          this.filterUnclearedToggle.value,
        ),
      )

    this.filterUnclearedToggle.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(unclearedTimestampToggle =>
        this.setClearedTimestampFilter(
          this.filterClearedToggle.value,
          unclearedTimestampToggle,
        ),
      )
  }

  setupPostDateTodayUpdate() {
    this.filterPostDateTodayToggle.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(async shouldFilterPostDateToday => {
        await this.setPostDateFilter(
          'equals',
          shouldFilterPostDateToday ? this.sessionReconDate : undefined,
        )
      })
  }

  setupPostDateNotFutureUpdate() {
    this.hideFuturePostDatesToggle.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(async shouldFilterPostDateNotFuture => {
        await this.setPostDateFilter(
          'lessThanOrEqual',
          shouldFilterPostDateNotFuture ? this.sessionReconDate : undefined,
        )
      })
  }

  async setClearedTimestampFilter(
    clearedTimestampToggle: boolean,
    unclearedTimestampToggle: boolean,
  ): Promise<void> {
    if (clearedTimestampToggle === unclearedTimestampToggle) {
      await this.setFilter('clearedTimestamp', null)
      return
    }

    const type = clearedTimestampToggle ? 'notBlank' : 'blank'
    const filter = {
      type,
      filterType: FilterType.DateTime,
      filter: '2023-01-01',
    }
    await this.setFilter('clearedTimestamp', filter)
  }

  /** @internal */
  async setPostDateFilter(
    type: string,
    filterDate: Date | undefined,
  ): Promise<void> {
    await this.setFilter(
      'postDate',
      filterDate
        ? {
            type,
            filterType: 'date',
            filter: formatDate(filterDate, ISODateFormat),
          }
        : null,
    )
  }

  setDeselectedRowsFromSelectAllFilter(rowId: string) {
    if (this.selectAllExcludedTranRows.has(rowId))
      this.selectAllExcludedTranRows.delete(rowId)
    else this.selectAllExcludedTranRows.add(rowId)
  }

  getSelectAllFilter() {
    return {
      rowId: {
        values: Array.from(this.selectAllExcludedTranRows.values()),
        notIn: true,
        filterType: 'set',
      },
    }
  }

  async setFilter(
    columnName: keyof ClearwaterTransaction,
    filter: unknown,
  ): Promise<void> {
    await setFilter<ClearwaterTransaction>(
      columnName,
      filter,
      this.gridApi?.api,
    )
  }

  private onFilterChanged(params: FilterChangedEvent) {
    this.onAssetIdFilterChanged(params)
    this.onPostDateFilterChanged(params)
    this.onHideFuturePostDatesFilterChanged(params)
    this.onClearedTimeStampChanged(params)
  }

  private onAssetIdFilterChanged(params: FilterChangedEvent) {
    const filterModel = params.api.getFilterModel()
    const assetIdValue = filterModel.secIdentifier?.filter
    const filterType = filterModel.secIdentifier?.type

    const assetIdsLength = assetIdValue?.split(',').length ?? 0
    if (filterType === FilterTypeForQuickFilterOfLength(assetIdsLength)) {
      this.quickSearchAssetIds.setValue(assetIdValue)
    } else {
      this.quickSearchAssetIds.reset('', { emitEvent: false })
    }
  }

  // Post Date Today filter should show transactions from yesterday
  private onPostDateFilterChanged(params: FilterChangedEvent) {
    const filterModel = params.api.getFilterInstance('postDate')?.getModel?.()
    const todayDateStr = formatDate(this.sessionReconDate, ISODateFormat)

    const isPostDateTodayFilter =
      filterModel &&
      filterModel.type === 'equals' &&
      filterModel.filter === todayDateStr
    this.filterPostDateTodayToggle.setValue(isPostDateTodayFilter, {
      emitEvent: false,
    })
  }

  // Post Date Today filter should show transactions from yesterday
  private onClearedTimeStampChanged(params: FilterChangedEvent) {
    const filterModel = params.api
      .getFilterInstance('clearedTimestamp')
      ?.getModel?.()
    if (filterModel) {
      const isCleared = filterModel.type === 'notBlank'
      const isUnCleared = filterModel.type === 'blank'
      this.filterClearedToggle.setValue(isCleared, {
        emitEvent: false,
      })
      this.filterUnclearedToggle.setValue(isUnCleared, {
        emitEvent: false,
      })
    }
  }

  private onHideFuturePostDatesFilterChanged(params: FilterChangedEvent) {
    const filterModel = params.api.getFilterInstance('postDate')?.getModel?.()
    const todayDateStr = formatDate(this.sessionReconDate, ISODateFormat)

    const isPostDateNotFutureFilter =
      filterModel &&
      filterModel.type === 'lessThanOrEqual' &&
      filterModel.filter === todayDateStr
    this.hideFuturePostDatesToggle.setValue(isPostDateNotFutureFilter, {
      emitEvent: false,
    })
  }

  clearAllFilters() {
    this.filterClearedToggle.setValue(false)
    this.filterUnclearedToggle.setValue(false)
    this.quickSearchAssetIds.reset('')
    this.fxCurrencyFilter.patchValue([])
    this.clearAllFiltersEnabled = false
    if (!this.cacheLoaded) return
    this.updateQuickSearchAssetIds()
    this.gridApi?.api.setFilterModel(null)
    this.gridApi?.api.onFilterChanged()
  }

  launchFiltersModal() {
    this.filterModalService.launchFiltersModal()
  }

  getCellToolTip(params: ITooltipParams): string {
    const colDef = params?.colDef as ColDef

    const validations = getColumnValidations({
      colDef,
      data: params?.data,
    })
    let tooltip =
      validations != null
        ? validations.map(v => v.message).join('\n')
        : params.valueFormatted ?? params.value

    const rowId = params?.node?.id
    const colField = colDef?.field

    if (rowId == null || colField == null) return tooltip
    if (DateColumn.includes(colField)) {
      tooltip = formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
    }

    const colDiff = this.differenceGetterService.getHasDifference(
      rowId,
      colField as keyof ClearwaterTransaction,
    )

    if (colDiff[0].hasDifference === false) {
      return tooltip
    }

    // Format to 4 decimal places

    let toolTipMsg = `Value: ${tooltip}`

    colDiff.forEach(diff => {
      if (typeof diff.amount === 'number') {
        toolTipMsg =
          toolTipMsg +
          `\n${diff.dataSourceType} Difference: ${diff.amount.toFixed(4)}`
      } else {
        toolTipMsg =
          toolTipMsg + `\n${diff.dataSourceType} Difference: ${diff.amount}`
      }
    })

    return toolTipMsg
  }

  /**
   * Cell style getter for column definitions
   *
   * @param params params used to resolve row id and field for column styling
   * @param styleCellDiff if true and column row id and field has a detected difference, will be highlighted red
   * @returns
   */
  getCellStyle(
    params: CellClassParams,
    styleCellDiff: boolean = false,
  ): CellStyle {
    const rowId = params?.node?.id
    const colField = params?.colDef?.field

    if (rowId == null || colField == null) return {}

    const highlightCellStyle: CellStyle = this.highlightService.getCellStyle(
      rowId,
      colField,
    )

    if (!(styleCellDiff && this.filterShowDiffsToggle.value))
      return highlightCellStyle

    const columnDifferenceResult =
      this.differenceGetterService.getHasDifference(
        rowId,
        colField as keyof ClearwaterTransaction,
      )
    if (columnDifferenceResult[0].hasDifference === false) {
      return highlightCellStyle
    }
    return {
      ...highlightCellStyle,
      color: 'red',
    }
  }

  /**
   * Same as getCellStyle, but automatically includes cellStyleDiff
   * Intended to reduce code duplication and improve test coverage
   *
   * @param params
   * @returns
   */
  getCellStyleWithDiff(params: CellClassParams) {
    return this.getCellStyle(params, true)
  }

  showDiffsToggled() {
    if (this.gridApi == null) return

    this.gridApi.api.redrawRows()

    if (!this.filterShowDiffsToggle.value) return

    const rowData: ClearwaterTransaction[] = []
    this.gridApi.api.forEachNode(node => {
      if (node.data) rowData.push(node.data)
    })

    if (rowData.length > 0)
      this.differenceGetterService.setRowDifferences(rowData, this.gridApi.api)
  }

  deduceRowClass(params: RowClassParams): string {
    let rowClass = this.highlightService.getRowClass(params.node.id ?? '')
    if (!rowClass) {
      rowClass = this.getDirectMatchRowClass(
        params.node.data?.extTranIds ?? [],
        params.node,
      )
    }
    return rowClass
  }
  getDirectMatchRowClass(extTranIds: number[], rowNode: RowNode): string {
    const shouldAddDirectMatch =
      !this.newSelectionRefresh &&
      this.externalTransactionSelected.find(extTrans =>
        extTranIds.includes(extTrans.extTranId),
      )
    if (shouldAddDirectMatch) {
      this.directMatchedTransactions.add(rowNode)
      return 'direct-match'
    } else {
      return ''
    }
  }

  private registerHotkeys() {
    const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th']
    for (let i = 0; i < ordinals.length; i++) {
      this.hotkeyDestroyStack.push(
        this.hotkeysService.bind(
          `highlight-row-with-${ordinals[i]}-color-in-cw-transactions` as HighlightRowsInCWTransactions,
          () =>
            this.highlightService.setHighlightOnExternalAction(this.gridApi, i),
        ),
      )
    }
  }
  private unregisterHotkeys() {
    this.hotkeyDestroyStack.forEach(hotkeyDestroyFn => hotkeyDestroyFn())
    this.hotkeyDestroyStack.length = 0
  }

  private async getAvailableRestrictedColumns(): Promise<
    TransactionAvailableRestrictedColumns[]
  > {
    return this.transactionsService
      .getAvailableRestrictedColumns()
      .pipe(
        takeUntil(this.destroySubject$),
        map(columns =>
          columns.map(column => {
            if (column.column === 'metaData') {
              return { column: 'metaData_8', isAvailable: column.isAvailable }
            } else return column
          }),
        ),
      )
      .toPromise()
  }

  private getCreateTxButton(
    category: TransactionCategory,
    enabled: boolean = true,
  ): CreateTxButton {
    const tooltip = enabled
      ? ''
      : `You do not have permission to create ${category} transactions`

    return {
      onClick: () => this.createActionButton(category),
      disabled: !enabled,
      name: category,
      tooltip,
    }
  }

  private getCreateTxButtons(
    permissions?: TransactionCreatePermissions,
  ): CreateTxButton[] {
    return [
      this.getCreateTxButton(
        TransactionCategory.CASH_ENTRY,
        permissions?.createCashEntry,
      ),
      this.getCreateTxButton(
        TransactionCategory.TRADE,
        permissions?.createTrade,
      ),
      this.getCreateTxButton(
        TransactionCategory.FX_SPOT_TRADE,
        permissions?.createFxSpotTrade,
      ),
      this.getCreateTxButton(
        TransactionCategory.CURRENCY_FORWARD,
        permissions?.createCurrencyForward,
      ),
      this.getCreateTxButton(
        TransactionCategory.WIRE_TRANSFER,
        permissions?.createWireTransfer,
      ),
      this.getCreateTxButton(
        TransactionCategory.DIRECT_TRANSFER,
        permissions?.createDirectTransfer,
      ),
      this.getCreateTxButton(
        TransactionCategory.SECURITY_TRANSFER,
        permissions?.createSecurityTransfer,
      ),
    ]
  }
}
